import React, { useState, useEffect } from "react";
import { useLocalStorage } from "./hooks";
import {
  LayoutDashboard, Users, Loader2, Settings as SettingsIcon, LogOut,
  ChevronDown, HardHat, Lock, Library, FolderOpen, ShieldCheck, HelpCircle,
  X, MessageSquare, AlertCircle, Bell, Check, Building2, Plus, RefreshCw,
  UserPlus, FileText, Activity
} from "lucide-react";
import { db } from "./lib/firebase";
import { addDoc, collection } from "firebase/firestore";

// Imports des contexts
import {
  AuthProvider, useAuth,
  CompanyProvider, useCompany,
  NotificationProvider, useNotifications,
  NavigationProvider, useNavigation
} from "./contexts";

// Imports des composants
import AuthPage from "./components/AuthPage";
import BetaWelcomePage from "./components/BetaWelcomePage";
import Dashboard from "./components/Dashboard";
import DocumentsManager from "./components/DocumentsManager";
import ClientList from "./components/ClientList";
import Settings from "./components/Settings";
import Editor from "./components/Editor";
import ConstructionSites from "./components/ConstructionSites";
import PriceLibrary from "./components/PriceLibrary";
import AdminPanel from "./components/AdminPanel";
import InvitationAcceptPage from "./components/InvitationAcceptPage";
import Prospects from "./components/Prospects";
import Properties from "./components/Properties";
import LegalPage from "./components/LegalPage";
import CGUPage from "./components/CGUPage";
import ActivityLogs from "./components/ActivityLogs";
import InteractiveTutorial from "./components/InteractiveTutorial";
import ErrorBoundary from "./components/ui/ErrorBoundary";
import FeedbackModal from "./components/FeedbackModal";
import FeedbackAdmin from "./components/FeedbackAdmin";
import NotificationPrompt from "./components/NotificationPrompt";
import NotificationCenter from "./components/NotificationCenter";
import { CreateCompanyModal, InviteMemberModal, SupportModal } from "./components/modals";

import { Routes, Route } from 'react-router-dom';
import SignaturePage from './components/SignaturePage';

// --- COMPOSANTS INTERNES ---

const UpgradeRequired = ({ onUpgrade }) => (
  <div className="flex flex-col items-center justify-center h-[60vh] text-center p-6 animate-in fade-in zoom-in duration-300">
      <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mb-6 shadow-inner"><Lock size={48} className="text-slate-400"/></div>
      <h2 className="text-3xl font-bold text-slate-800 mb-3">Fonctionnalité Business</h2>
      <p className="text-slate-500 max-w-md mb-8 text-lg">Le suivi de chantier, les photos et la gestion des dépenses sont réservés aux membres Business.</p>
      <button onClick={onUpgrade} className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-4 rounded-xl font-bold shadow-lg hover:scale-105 transition transform text-lg">Passer en Business (15€/mois)</button>
  </div>
);

// --- HELPER: Traduction des rôles ---
const getRoleLabel = (role) => {
    const roles = { 'owner': 'Propriétaire', 'admin': 'Administrateur', 'manager': 'Chef d\'équipe', 'editor': 'Éditeur', 'member': 'Membre', 'viewer': 'Observateur' };
    return roles[role] || role || 'Membre';
};

// --- APP PRINCIPALE ---
export default function App() {
  const isSignaturePage = window.location.pathname.startsWith('/sign/');

  if (isSignaturePage) {
    return (
      <Routes>
        <Route path="/sign/:token" element={<SignaturePage />} />
      </Routes>
    );
  }

  return (
    <AuthProvider>
      <AppWithAuth />
    </AuthProvider>
  );
}

// Composant intermédiaire qui vérifie l'authentification
function AppWithAuth() {
  const { user, loading, subscriptionStatus, updateSubscriptionStatus } = useAuth();

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-blue-600 mb-4" size={48} />
        <p className="text-slate-500 font-medium">Démarrage...</p>
      </div>
    );
  }

  if (!user) {
    return <ErrorBoundary><AuthPage /></ErrorBoundary>;
  }

  // Vérifier si invitation en attente
  const params = new URLSearchParams(window.location.search);
  const invitationToken = params.get('token') || params.get('t');
  if (invitationToken) {
    return <ErrorBoundary><InvitationAcceptPage user={user} /></ErrorBoundary>;
  }

  return (
    <CompanyProvider>
      <AppWithCompany />
    </CompanyProvider>
  );
}

// Composant qui vérifie l'entreprise
function AppWithCompany() {
  const { user, subscriptionStatus, updateSubscriptionStatus } = useAuth();
  const { loadingCompanies, hasCompany } = useCompany();

  if (loadingCompanies) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-blue-600 mb-4" size={48} />
        <p className="text-slate-500 font-medium">Vérification de vos affiliations...</p>
      </div>
    );
  }

  if (!hasCompany && subscriptionStatus === 'inactive') {
    return (
      <ErrorBoundary>
        <BetaWelcomePage
          user={user}
          onComplete={() => {
            updateSubscriptionStatus('active');
            window.location.reload();
          }}
        />
      </ErrorBoundary>
    );
  }

  return (
    <NotificationProvider>
      <NavigationProvider>
        <MainApp />
      </NavigationProvider>
    </NotificationProvider>
  );
}

// Composant principal avec toute la logique d'interface
function MainApp() {
  const { user, userPlan, isAdmin, logout } = useAuth();
  const {
    activeCompanyId, setActiveCompanyId, userCompanies, loadingCompanies,
    activeCompany, userRole, canAccessSites, canAccessFinancial, canManageCompanies,
    isRepairing, handleRepairProfile
  } = useCompany();
  const { notifications, unreadCount, markAsRead, markAllAsRead, showNotificationPrompt, dismissNotificationPrompt } = useNotifications();
  const { activeTab, viewMode, currentDocId, docType, prospectData, selectedSiteId, handleNavigate, handleOpenEditor, handleBackToList, setActiveTab } = useNavigation();

  // États UI locaux
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showCompanySelector, setShowCompanySelector] = useState(false);
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [showInviteMember, setShowInviteMember] = useState(false);
  const [tutorialCompleted, setTutorialCompleted] = useLocalStorage('ovrio-tutorial-completed', false);
  const [darkMode, setDarkMode] = useLocalStorage('ovrio-darkmode', false);
  const showTutorial = !tutorialCompleted;

  // Effet pour gérer le mode sombre (classe CSS uniquement)
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Fonction pour logger l'activité utilisateur
  const logActivity = async (action, details = {}) => {
    if (!user || !activeCompanyId) return;
    try {
      await addDoc(collection(db, 'companies', activeCompanyId, 'activityLogs'), {
        userId: user.uid,
        userEmail: user.email,
        action,
        details,
        timestamp: new Date().toISOString(),
        createdAt: new Date()
      });
    } catch (e) {
      console.error('Error logging activity:', e);
    }
  };

  const handleLogout = () => { logout(); setShowUserMenu(false); };

  const handleCompanyCreated = (companyId) => {
    setActiveCompanyId(companyId);
    setShowCreateCompany(false);
  };

  if (loadingCompanies) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-blue-600 mb-4" size={48} />
        <h3 className="font-bold text-slate-800 mb-1">Recherche de votre entreprise...</h3>
      </div>
    );
  }

  const menuItems = [
    { id: "dashboard", label: "Accueil", icon: LayoutDashboard },
    { id: "prospects", label: "Prospects", icon: UserPlus },
    { id: "properties", label: "Biens", icon: Building2 },
    { id: "chantiers", label: "Chantiers", icon: HardHat },
  ];

  if (canAccessFinancial) {
    menuItems.push({ id: "gestion", label: "Devis & Factures", icon: FolderOpen });
  }

  menuItems.push(
    { id: "library", label: "Catalogue", icon: Library },
    { id: "clients", label: "Clients", icon: Users }
  );

  if (isAdmin) menuItems.push({ id: "admin", label: "Admin", icon: ShieldCheck });

  return (
    <ErrorBoundary>
      <div className={`min-h-screen font-sans pb-24 md:pb-10 transition-colors duration-200 ${darkMode ? 'bg-slate-900 text-slate-100' : 'bg-slate-50 text-slate-800'}`} onClick={() => { setShowUserMenu(false); setShowNotifications(false); }}>

        {/* Tutoriel interactif au premier lancement */}
        {showTutorial && activeCompanyId && (
          <InteractiveTutorial
            onComplete={() => { setTutorialCompleted(true); logActivity('tutorial_completed'); }}
            onSkip={() => { setTutorialCompleted(true); logActivity('tutorial_skipped'); }}
          />
        )}

        {/* Modales */}
        {showCreateCompany && <CreateCompanyModal onClose={() => setShowCreateCompany(false)} onCompanyCreated={handleCompanyCreated} />}
        {showInviteMember && <InviteMemberModal companyId={activeCompanyId} companyName={activeCompany?.name} onClose={() => setShowInviteMember(false)} onInviteSent={() => {}} />}
        {showSupport && <SupportModal onClose={() => setShowSupport(false)} />}
        {showFeedback && <FeedbackModal user={user} onClose={() => setShowFeedback(false)} />}

        {/* Prompt de notification push */}
        {showNotificationPrompt && (
          <NotificationPrompt
            user={user}
            activeCompanyId={activeCompanyId}
            onPermissionGranted={dismissNotificationPrompt}
          />
        )}

        {/* Bouton flottant Feedback */}
        <button onClick={() => setShowFeedback(true)} className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-40 bg-gradient-to-r from-orange-500 to-pink-500 text-white p-3 md:p-4 rounded-full shadow-2xl hover:shadow-orange-500/50 hover:scale-110 transition-all print:hidden flex items-center gap-2 group animate-pulse hover:animate-none" title="Envoyer un retour">
          <MessageSquare size={20} className="md:hidden"/><MessageSquare size={24} className="hidden md:block"/>
          <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap font-bold">Feedback Bêta</span>
        </button>

        {/* Nav Desktop */}
        <nav className="bg-slate-900 text-white p-4 sticky top-0 z-50 shadow-xl print:hidden hidden md:block">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-4"><div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center font-bold text-xl cursor-pointer" onClick={() => handleNavigate('dashboard')}>O</div><div><h1 className="font-bold text-lg tracking-wide text-white">Ovrio</h1><p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Logiciel d'Artisan</p></div></div>
            <div className="flex bg-slate-800/50 p-1 rounded-xl backdrop-blur-sm border border-slate-700/50">{menuItems.map(item => (<NavButton key={item.id} {...item} activeTab={activeTab} onClick={() => handleNavigate(item.id)} />))}</div>
            <div className="flex items-center gap-4">
                {!isAdmin && <button onClick={() => setShowSupport(true)} className="text-slate-400 hover:text-white flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-lg hover:bg-slate-800 transition"><HelpCircle size={16}/> Aide</button>}

                {userCompanies.length > 0 && (
                  <div className="relative">
                    <button onClick={(e) => { e.stopPropagation(); setShowCompanySelector(!showCompanySelector); }} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition text-xs font-medium">
                      <Building2 size={16} />{activeCompany?.name || 'Entreprise'}<ChevronDown size={14} />
                    </button>
                    {showCompanySelector && (
                      <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl py-2 text-slate-700 border border-slate-100 z-50 animate-in fade-in zoom-in-95">
                        {userCompanies.map((company) => (
                          <button key={company.id} onClick={() => { setActiveCompanyId(company.id); setShowCompanySelector(false); }} className={`w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 transition-colors text-sm ${activeCompanyId === company.id ? 'bg-blue-50 text-blue-600 font-medium' : ''}`}>
                            <Building2 size={16} /><div className="flex-1"><p className="font-medium">{company.name}</p><p className="text-[10px] text-slate-500">{getRoleLabel(company.role)}</p></div>{activeCompanyId === company.id && <Check size={16} className="text-blue-600" />}
                          </button>
                        ))}
                        <button onClick={() => { setShowCompanySelector(false); setShowInviteMember(true); }} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 transition-colors text-sm border-t border-slate-50 text-green-600 font-medium"><UserPlus size={16} /> Ajouter un membre</button>
                      </div>
                    )}
                  </div>
                )}

                <button onClick={() => { setDarkMode(!darkMode); logActivity('toggle_dark_mode', { enabled: !darkMode }); }} className="w-9 h-9 bg-slate-800 rounded-full flex items-center justify-center text-slate-300 hover:text-white hover:bg-slate-700 transition" title={darkMode ? "Mode clair" : "Mode sombre"}>{darkMode ? '☀️' : '🌙'}</button>

                <div className="relative">
                    <button onClick={(e) => { e.stopPropagation(); setShowNotifications(!showNotifications); setShowUserMenu(false); }} className="w-9 h-9 bg-slate-800 rounded-full flex items-center justify-center text-slate-300 hover:text-white hover:bg-slate-700 transition relative"><Bell size={18}/>{unreadCount > 0 && <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-slate-900 animate-pulse"></span>}</button>
                    {showNotifications && <NotificationCenter notifications={notifications} onClose={() => setShowNotifications(false)} onNavigate={handleNavigate} onMarkAsRead={markAsRead} onMarkAllRead={markAllAsRead}/>}
                </div>

                <div className="relative">
                  <div className="flex items-center gap-3 text-slate-400 cursor-pointer hover:text-white transition p-1 rounded-lg hover:bg-slate-800" onClick={(e) => { e.stopPropagation(); setShowUserMenu(!showUserMenu); setShowNotifications(false); }}>
                    <div className="text-right hidden lg:block"><p className="text-xs font-bold text-white">{user.email}</p><p className={`text-[10px] font-bold ${isAdmin ? 'text-purple-400' : 'text-blue-400'}`}>{activeCompanyId && activeCompany ? `Connecté à ${activeCompany.name}` : (isAdmin ? 'Super Admin' : userPlan)}</p></div><ChevronDown size={14} />
                  </div>
                  {showUserMenu && (
                    <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl py-2 text-slate-700 border border-slate-100 overflow-hidden z-50 animate-in fade-in zoom-in-95">
                      <button onClick={() => setActiveTab('settings')} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 transition-colors text-sm font-medium"><SettingsIcon size={16} className="text-slate-400"/> Paramètres</button>
                      <button onClick={() => { setActiveTab('activity'); setShowUserMenu(false); }} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 transition-colors text-sm font-medium border-t border-slate-50"><Activity size={16} className="text-slate-400"/> Journal d'activité</button>
                      {isAdmin && <button onClick={() => { setActiveTab('feedbacks'); setShowUserMenu(false); }} className="w-full text-left px-4 py-3 hover:bg-gradient-to-r hover:from-orange-50 hover:to-pink-50 flex items-center gap-3 transition-colors text-sm font-medium border-t border-slate-50"><MessageSquare size={16} className="text-orange-500"/> Feedbacks Bêta<span className="ml-auto bg-orange-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">ADMIN</span></button>}
                      <button onClick={() => { setActiveTab('legal'); setShowUserMenu(false); }} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 transition-colors text-sm font-medium border-t border-slate-50"><ShieldCheck size={16} className="text-slate-400"/> Mentions Légales</button>
                      <button onClick={() => { setActiveTab('cgu'); setShowUserMenu(false); }} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 transition-colors text-sm font-medium border-t border-slate-50"><FileText size={16} className="text-slate-400"/> CGU / CGV</button>
                      <button onClick={handleLogout} className="w-full text-left px-4 py-3 hover:bg-red-50 text-red-600 flex items-center gap-3 transition-colors text-sm font-medium border-t border-slate-50"><LogOut size={16} /> Déconnexion</button>
                    </div>
                  )}
                </div>
            </div>
          </div>
        </nav>

        {/* Nav Mobile */}
        <nav className="bg-slate-900 text-white p-4 sticky top-0 z-50 shadow-md md:hidden flex justify-between items-center print:hidden">
          <div className="flex items-center gap-2" onClick={() => handleNavigate('dashboard')}><div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center font-bold text-sm">O</div><span className="font-bold tracking-wide">Ovrio</span></div>
          <div className="flex items-center gap-3">
            {!isAdmin && <button onClick={() => setShowSupport(true)}><HelpCircle size={20} className="text-slate-400"/></button>}
            <div className="relative">
              <button onClick={(e) => { e.stopPropagation(); setShowNotifications(!showNotifications); setShowUserMenu(false); }} className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center text-slate-300 active:scale-95 transition relative"><Bell size={16}/>{unreadCount > 0 && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-slate-900 animate-pulse"></span>}</button>
              {showNotifications && <NotificationCenter notifications={notifications} onClose={() => setShowNotifications(false)} onNavigate={handleNavigate} onMarkAsRead={markAsRead} onMarkAllRead={markAllAsRead}/>}
            </div>
            <div className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center active:scale-95 transition" onClick={() => setActiveTab('settings')}><SettingsIcon size={16} /></div>
          </div>
        </nav>

        {/* Contenu principal */}
        <main className="pt-4 md:pt-8 px-2 md:px-0">
          {!activeCompanyId && userCompanies.length === 0 ? (
            <div className="max-w-2xl mx-auto mt-16 p-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-200 text-center animate-in fade-in">
              <div className="flex justify-center mb-4"><div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center"><Building2 size={32} className="text-blue-600" /></div></div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Bienvenue dans Ovrio</h2>
              <p className="text-slate-600 mb-6">Si vous ne voyez pas votre entreprise, cliquez sur "Resynchroniser".</p>
              <div className="flex flex-col md:flex-row items-center justify-center gap-4">
                {canManageCompanies ? (
                  <button onClick={() => setShowCreateCompany(true)} className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition inline-flex items-center gap-2"><Plus size={20} /> Créer une Entreprise</button>
                ) : (
                  <div className="px-6 py-3 bg-slate-100 text-slate-500 font-medium rounded-lg border border-slate-300 inline-flex items-center gap-2"><Lock size={20} /> Créer une Entreprise (Business uniquement)</div>
                )}
                <button onClick={handleRepairProfile} disabled={isRepairing} className="px-6 py-3 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-medium rounded-lg transition inline-flex items-center gap-2">{isRepairing ? <Loader2 size={20} className="animate-spin" /> : <RefreshCw size={20} />} {isRepairing ? "Recherche..." : "Resynchroniser mon profil"}</button>
              </div>
            </div>
          ) : !activeCompanyId ? (
            <div className="max-w-2xl mx-auto mt-12 p-6 bg-orange-50 rounded-2xl border border-orange-200 flex items-start gap-4 animate-in fade-in">
              <AlertCircle className="text-orange-600 flex-shrink-0 mt-1" size={24} />
              <div><h3 className="font-bold text-orange-900 mb-1">Sélectionnez une entreprise</h3><p className="text-sm text-orange-700">Vous avez plusieurs entreprises. Veuillez en sélectionner une dans le menu pour continuer.</p></div>
            </div>
          ) : (
            <>
              {activeTab === "dashboard" && <ErrorBoundary><Dashboard user={user} activeCompanyId={activeCompanyId} userRole={userRole} userPlan={userPlan} onNavigate={handleNavigate} onCreateDocument={handleOpenEditor} logActivity={logActivity} /></ErrorBoundary>}
              {activeTab === "chantiers" && <ErrorBoundary>{canAccessSites ? <ConstructionSites user={user} activeCompanyId={activeCompanyId} initialSiteId={selectedSiteId} /> : <UpgradeRequired onUpgrade={() => {}} />}</ErrorBoundary>}
              {activeTab === "gestion" && viewMode === "list" && <ErrorBoundary><DocumentsManager user={user} activeCompanyId={activeCompanyId} onOpenEditor={handleOpenEditor} logActivity={logActivity} /></ErrorBoundary>}
              {activeTab === "clients" && <ErrorBoundary><ClientList user={user} activeCompanyId={activeCompanyId} onSelectClient={(client) => handleOpenEditor(null, "quote")} logActivity={logActivity} /></ErrorBoundary>}
              {activeTab === "prospects" && <ErrorBoundary><Prospects user={user} activeCompanyId={activeCompanyId} onCreateQuote={(prospectData) => handleOpenEditor(null, "quote", prospectData)} /></ErrorBoundary>}
              {activeTab === "properties" && <ErrorBoundary><Properties user={user} activeCompanyId={activeCompanyId} /></ErrorBoundary>}
              {activeTab === "library" && <ErrorBoundary><PriceLibrary user={user} activeCompanyId={activeCompanyId} /></ErrorBoundary>}
              {activeTab === "settings" && <ErrorBoundary><Settings user={user} activeCompanyId={activeCompanyId} activeCompanyName={activeCompany?.name || 'Non défini'} /></ErrorBoundary>}
              {activeTab === "activity" && <ErrorBoundary><ActivityLogs user={user} activeCompanyId={activeCompanyId} onBack={() => setActiveTab('dashboard')} /></ErrorBoundary>}
              {activeTab === "feedbacks" && <ErrorBoundary><FeedbackAdmin user={user} /></ErrorBoundary>}
              {activeTab === "legal" && <ErrorBoundary><LegalPage onBack={() => setActiveTab('dashboard')} /></ErrorBoundary>}
              {activeTab === "cgu" && <ErrorBoundary><CGUPage onBack={() => setActiveTab('dashboard')} /></ErrorBoundary>}
              {activeTab === "admin" && isAdmin && <ErrorBoundary><AdminPanel /></ErrorBoundary>}
              {viewMode === "edit" && (activeTab === "gestion" || activeTab === "devis" || activeTab === "factures") && <ErrorBoundary><Editor user={user} activeCompanyId={activeCompanyId} docId={currentDocId} type={docType} prospectData={prospectData} onBack={handleBackToList} onSaveSuccess={() => {}} onRedirect={handleOpenEditor} logActivity={logActivity} /></ErrorBoundary>}
            </>
          )}
        </main>

        {/* Bottom Nav Mobile */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-2 flex justify-between items-center z-50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] print:hidden safe-area-pb">
          {menuItems.map(item => (
            <button key={item.id} onClick={() => handleNavigate(item.id)} className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all ${activeTab === item.id ? 'text-blue-600' : 'text-slate-400'}`}>
              {item.id === 'chantiers' && !canAccessSites && activeTab !== 'chantiers' && <div className="absolute top-2 right-4 w-2 h-2 bg-orange-500 rounded-full"></div>}
              <item.icon size={24} strokeWidth={activeTab === item.id ? 2.5 : 2} /><span className="text-[10px] font-medium">{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    </ErrorBoundary>
  );
}

const NavButton = ({ label, icon: Icon, activeTab, onClick, id }) => (
  <button onClick={onClick} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${activeTab === id ? "bg-slate-700 text-white shadow-md" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}><Icon size={16} className={activeTab === id ? "text-orange-400" : ""} /> {label}</button>
);
